import { Log } from "@/types/app/log";

type InputsType = Record<string, string | number | null>;

/**
 * Extracts the inputs that were used in generating the Evaluator Log.
 *
 * For an LLM Evaluator, we can use the inputs that were used in the Prompt.
 * For other Evaluator types (e.g. Python), this will include all inputs as
 * there is no way to determine which inputs were used.
 *
 * Note that this only handles a subset of the available variables:
 * - `inputs`
 * - `messages`
 * - `output`
 *
 * This leaves out other fields like `created_at`, `updated_at`, `provider_response`, `provider_latency`, etc.
 *
 * TODO: Investigate improving this with schemas.
 */
export const getInputsFromEvaluatorLog = ({
  log,
  evaluatorLog,
}: {
  log: Log;
  evaluatorLog?: Pick<Log, "inputs"> | null;
}): Record<string, string | number | null> => {
  if (evaluatorLog?.inputs && Object.keys(evaluatorLog.inputs).length > 0) {
    // If this is an LLM or Code Evaluator, we have a known set of inputs.
    return evaluatorLog.inputs;
  }

  let inputs: InputsType = {
    ...(log.inputs
      ? Object.fromEntries(Object.entries(log.inputs).map(([key, value]) => [`log.inputs.${key}`, value]))
      : []),
    ...("messages" in log && log.messages
      ? Object.fromEntries(
          log.messages.map((message, i) => [
            `log.messages[${i}].content`,
            typeof message.content === "string" ? message.content : "", // This doesn't handle messages with object content (e.g. images)
          ]),
        )
      : []),
    "log.output": log.output,
  } as InputsType;

  return inputs;
};
